import * as React from 'react';
import { compose, setDisplayName, withState, withHandlers, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { drawLinesOnCanvas } from './drawLinesOnCanvas';
import { hitTestLines, HitTestResult } from './hitTestLines';
import { calculateDrawData, DrawData } from 'ui/shared/calculateDrawData';
import { Point } from 'logic/reports/doLinesIntersect';

import './LineEditor.css';
import { imageElementFromBase64Jpeg } from 'ui/shared/imageElementFromBase64Jpeg';

export type OwnProps = {
    showLines: boolean;
    points: ReadonlyArray<Point>;
    selectedIndex: number | null;
    image: string | null;
    disabled: boolean;
    onChange: (i: number, pt: Point) => void;
    onAddPoint: (pt: Point) => void;
    onSelect: (i: number | null) => void;
};

const RawLineEditor = ({
    disabled,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleClick,
    styles,
    t,
}: OwnProps & RecomposeProps & OemProps & TranslateProps) =>
    <canvas
        id="line-editor"
        className={ disabled ? 'line-editor-disabled' : undefined }
        style={ styles.Bordered }
        onMouseDown={ handleMouseDown }
        onMouseMove={ handleMouseMove }
        onMouseUp={ handleMouseUp }
        onClick={ handleClick }
    />
;

const getCanvas = () => document.getElementById('line-editor') as HTMLCanvasElement | null;

type MouseDownData = {
    hitTest: HitTestResult;
    x: number;
    y: number;
};

type RecomposeProps = {
    backgroundImage: HTMLImageElement | null;
    handleMouseDown: React.MouseEventHandler<HTMLCanvasElement>;
    handleMouseMove: React.MouseEventHandler<HTMLCanvasElement>;
    handleMouseUp: React.MouseEventHandler<HTMLCanvasElement>;
    handleClick: React.MouseEventHandler<HTMLCanvasElement>;
    handleLoadImage: () => void;
};
type _RecomposeProps = RecomposeProps & {
    _editPoint: Point | null;
    _redraw: boolean;
    _drawData: DrawData;
    _mouseDown: MouseDownData | null;
    _setEditPont: (pt: Point | null) => void;
    _setRedraw: (b: boolean) => void;
    _setDrawData: (d: DrawData) => void;
    _setMouseDown: (d: MouseDownData | null) => void;
    _setBackgroundImage: (i: HTMLImageElement | null) => void;
};

const setCursor = (canvas: HTMLCanvasElement, result: HitTestResult) => {
    canvas.style.cursor =
        typeof result === 'number'
            ? 'move'
            : 'copy';
};

// Must treat the lines array and zone elements as immutable... so we need to
// create a new array with a new element when a zone is being edited.
const replacePoint = (points: ReadonlyArray<Point>, selectedIndex: number | null, editPoint: Point | null) =>
    editPoint
        ? points.map((pt, i) => i === selectedIndex ? editPoint : pt)
        : points
;

export const LineEditor = compose<OwnProps & RecomposeProps & OemProps & TranslateProps, OwnProps>(
    setDisplayName('LineEditor'),
    skin,
    translate(),
    withState('backgroundImage', '_setBackgroundImage', null),
    withState('_redraw', '_setRedraw', true),
    withState('_drawData', '_setDrawData', calculateDrawData(null, null)),
    withState('_mouseDown', '_setMouseDown', null),
    withState('_editPoint', '_setEditPont', null),
    withHandlers<OwnProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleMouseDown: ({ points, _drawData, _setMouseDown, onAddPoint, onSelect, disabled }) => (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (disabled) {
                return;
            }
            const canvas = getCanvas();
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const x = Math.round((e.pageX - rect.left - _drawData.xOffset) / _drawData.scale);
            const y = Math.round((e.pageY - rect.top - _drawData.yOffset) / _drawData.scale);

            const hitTest = hitTestLines(x, y, canvas, _drawData, points);

            if (typeof hitTest === 'number') {
                onSelect(hitTest);
                _setMouseDown(typeof hitTest === 'number' ? { hitTest, x, y } : null);
                setCursor(canvas, hitTest);
            }
            else {
                onAddPoint({ x, y });
                _setMouseDown({ hitTest: points.length, x, y });
                setCursor(canvas, points.length);
            }
        },
        handleMouseMove: ({ points, backgroundImage, _setEditPont, _drawData, _mouseDown, _setRedraw, disabled }) => (e: React.MouseEvent<HTMLCanvasElement>) => { // tslint:disable-line
            if (disabled) {
                return;
            }
            const canvas = getCanvas();
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const x = Math.round((e.pageX - rect.left - _drawData.xOffset) / _drawData.scale);
            const y = Math.round((e.pageY - rect.top - _drawData.yOffset) / _drawData.scale);

            if (_mouseDown) { // User is dragging something
                const { hitTest = null } = _mouseDown;
                const xOffset = x - _mouseDown.x;
                const yOffset = y - _mouseDown.y;
                const xMax = backgroundImage ? backgroundImage.width : Math.round(canvas.width / _drawData.scale);
                const yMax = backgroundImage ? backgroundImage.height : Math.round(canvas.height / _drawData.scale);
                if (typeof hitTest === 'number' && points[hitTest]) {
                    _setEditPont({
                        x: Math.max(0, Math.min(points[hitTest].x + xOffset, xMax)),
                        y: Math.max(0, Math.min(points[hitTest].y + yOffset, yMax)),
                    });
                    _setRedraw(true);
                }
            }
            else { // User is just moving the mouse
                const hitTest = hitTestLines(x, y, canvas, _drawData, points);
                setCursor(canvas, hitTest);
            }
        },
        handleMouseUp: ({ onChange, selectedIndex, _editPoint, _mouseDown, _setMouseDown, _setEditPont, _setRedraw, disabled }) => (e: React.MouseEvent<HTMLCanvasElement>) => { // tslint:disable-line
            if (disabled) {
                return;
            }
            if (_mouseDown && typeof selectedIndex === 'number' && _editPoint) {
                onChange(selectedIndex, _editPoint);
            }
            _setMouseDown(null);
            _setEditPont(null);
            _setRedraw(true);
        },
        handleClick: ({ points, _drawData, _setRedraw, onSelect, disabled }) => (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (disabled) {
                return;
            }
            const canvas = getCanvas();
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const x = Math.round((e.pageX - rect.left - _drawData.xOffset) / _drawData.scale);
            const y = Math.round((e.pageY - rect.top - _drawData.yOffset) / _drawData.scale);
            
            const hitTest = hitTestLines(x, y, canvas, _drawData, points);

            onSelect(hitTest);
            _setRedraw(true);
        },
        handleLoadImage: ({ image, _setBackgroundImage, _setRedraw, _setDrawData }) => async () => {
            const img = await imageElementFromBase64Jpeg(image);
            _setBackgroundImage(img);

            const canvas = getCanvas();
            const drawData = calculateDrawData(canvas, img);
            _setDrawData(drawData);
            _setRedraw(true);
        },
    }),
    lifecycle<OwnProps & _RecomposeProps & TranslateProps, {}>({
        componentDidMount() {
            const canvas = getCanvas();
            if (canvas) {
                // Set the dimensions (or else the drawings will be at the wrong scale)
                canvas.width = canvas.clientWidth;
                canvas.height = canvas.clientHeight;
                
                const { _setDrawData } = this.props;
                const drawData = calculateDrawData(canvas, null);
                _setDrawData(drawData);
            }

            // Start loading the image if we were given a base64 image string
            const { image, handleLoadImage } = this.props;
            if (image) {
                handleLoadImage();
            }
        },
        componentDidUpdate(oldProps) {
            const { points, selectedIndex, showLines, image, _redraw, _setRedraw, backgroundImage, handleLoadImage } = this.props;

            // Redraw if certain properties changed since the last render
            if (points !== oldProps.points ||
                selectedIndex !== oldProps.selectedIndex ||
                showLines !== oldProps.showLines ||
                backgroundImage !== oldProps.backgroundImage
            ) {
                _setRedraw(true);
            }

            // image is the base64 image string but we need the actual image element
            // to paint onto the canvas. So if the image string changed, start the
            // process of reading the string into an <img/>
            if (image !== oldProps.image) {
                handleLoadImage();
            }

            // Only redraw when explicitly told to do so
            if (!_redraw) {
                return;
            }
            _setRedraw(false);
            
            const { _editPoint, _drawData, t } = this.props;
            const linesToDraw = replacePoint(points, selectedIndex, _editPoint);
            const canvas = getCanvas();
            drawLinesOnCanvas(canvas, _drawData, backgroundImage, showLines ? linesToDraw : [], selectedIndex, t('3006'), t('2845'));
        },
    }),
)(RawLineEditor);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/line/LineEditor.tsx